/**
 * SAT · Bewerbungsformular V2 · /sat-master/bewerbung + /sat-mentor/bewerbung
 * Serverseitige Validierung, Datenschutz-Consent versioniert, Marketing-Consent optional (default OFF).
 * Keine sensiblen medizinischen Details erlaubt.
 */
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { S, Section, PrimaryCta, FooterDisclaimer } from "../components/salespage/SalesSections";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApplicationForm({ programme: fixedProgramme }) {
  const params = useParams();
  const navigate = useNavigate();
  const programme = fixedProgramme || (params.programme?.toUpperCase() === "SAT_MENTOR" ? "SAT_MENTOR" : "SAT_MASTER");
  const isMentor = programme === "SAT_MENTOR";

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    country: "Schweiz", timezone: "Europe/Zurich",
    motivation: "", desired_development: "", practical_readiness: "",
    time_available: "", live_participation_possible: false,
    prior_training: "", master_status: "", guidance_experience: "",
    practice_time: "", supervision_readiness: false,
    privacy_consent_accepted: false, marketing_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [serverError, setServerError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Vorname erforderlich";
    if (!form.last_name.trim()) e.last_name = "Nachname erforderlich";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Gültige E-Mail erforderlich";
    if (!form.country.trim()) e.country = "Land erforderlich";
    if (!form.timezone.trim()) e.timezone = "Zeitzone erforderlich";
    if (form.motivation.trim().length < 20) e.motivation = "Mindestens 20 Zeichen";
    if (form.desired_development.trim().length < 20) e.desired_development = "Mindestens 20 Zeichen";
    if (form.practical_readiness.trim().length < 5) e.practical_readiness = "Bitte kurz beschreiben";
    if (form.time_available.trim().length < 5) e.time_available = "Bitte angeben";
    if (!form.privacy_consent_accepted) e.privacy_consent_accepted = "Datenschutz-Einwilligung erforderlich";
    if (isMentor) {
      if (form.prior_training.trim().length < 3) e.prior_training = "Bitte deine Vorbildung nennen";
      if (form.master_status.trim().length < 3) e.master_status = "Bitte deinen MASTER-Status nennen";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (evt) => {
    evt.preventDefault();
    setServerError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...form, programme };
      const r = await fetch(`${API}/api/applications/v2/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setSuccess(data.id);
      } else {
        setServerError(data.detail || "Ein Fehler ist aufgetreten");
      }
    } catch (err) {
      setServerError("Netzwerkfehler. Bitte erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ background: S.midnight, minHeight: "100vh" }}>
        <Helmet><title>Bewerbung eingereicht</title><meta name="robots" content="noindex,nofollow" /></Helmet>
        <Section id="thanks" eyebrow="Danke" title="Deine Bewerbung ist bei uns eingegangen">
          <p className="text-base leading-relaxed" style={{ color: S.ivoryMuted }} data-testid="application-success">
            Wir sichten jede Bewerbung persönlich. Innerhalb von 48 Stunden meldet sich unser Team bei dir.
          </p>
          <div className="mt-8">
            <PrimaryCta href="/" label="Zur Startseite" testId="success-home-cta" />
          </div>
        </Section>
        <FooterDisclaimer />
      </div>
    );
  }

  const inputStyle = {
    background: S.midnightSoft, color: S.ivory,
    border: `1px solid ${S.line}`,
    padding: "0.85rem 1rem", width: "100%", fontSize: "0.95rem",
  };
  const labelStyle = { color: S.goldSoft, fontSize: "0.85rem", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.5rem", display: "block" };
  const HintNoMedical = () => (
    <p className="text-xs mb-2" style={{ color: "rgba(246,241,231,0.5)" }}>
      Bitte hier <strong>keine</strong> Diagnosen, Traumadetails, Laborwerte,
      Medikamentendaten, Tagebuchinhalte oder andere besonders sensible medizinische Daten eintragen.
    </p>
  );

  return (
    <div style={{ background: S.midnight, minHeight: "100vh", color: S.ivory }} data-testid="page-application">
      <Helmet>
        <title>{`${isMentor ? "SAT MENTOR" : "SAT MASTER"} · Bewerbung`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Section
        id="app-hero"
        eyebrow={isMentor ? "SAT MENTOR · Bewerbung" : "SAT MASTER · Platzanfrage"}
        title={isMentor ? "Für SAT MENTOR bewerben" : "Meinen Platz anfragen"}
        intro="Nimm dir Zeit für diese Anfrage. Wir sichten jede persönlich und antworten innerhalb von 48 Stunden."
      >
        {serverError && (
          <div
            className="p-4 mb-6 border"
            style={{ borderColor: "#B85555", color: "#F1B5B5", background: "rgba(184,85,85,0.08)" }}
            data-testid="server-error"
          >
            {typeof serverError === "string" ? serverError : JSON.stringify(serverError)}
          </div>
        )}
        <form onSubmit={submit} noValidate className="space-y-6" data-testid="application-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label style={labelStyle}>Vorname *</label>
              <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                style={inputStyle} data-testid="input-first-name" required aria-invalid={!!errors.first_name} />
              {errors.first_name && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }} data-testid="err-first-name">{errors.first_name}</p>}
            </div>
            <div>
              <label style={labelStyle}>Nachname *</label>
              <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                style={inputStyle} data-testid="input-last-name" required aria-invalid={!!errors.last_name} />
              {errors.last_name && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }} data-testid="err-last-name">{errors.last_name}</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label style={labelStyle}>E-Mail *</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                style={inputStyle} data-testid="input-email" required aria-invalid={!!errors.email} />
              {errors.email && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }} data-testid="err-email">{errors.email}</p>}
            </div>
            <div>
              <label style={labelStyle}>Telefon (optional)</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                style={inputStyle} data-testid="input-phone" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label style={labelStyle}>Wohnland *</label>
              <input value={form.country} onChange={(e) => set("country", e.target.value)}
                style={inputStyle} data-testid="input-country" required />
              {errors.country && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }}>{errors.country}</p>}
            </div>
            <div>
              <label style={labelStyle}>Zeitzone *</label>
              <input value={form.timezone} onChange={(e) => set("timezone", e.target.value)}
                style={inputStyle} data-testid="input-timezone" required />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Deine Motivation *</label>
            <HintNoMedical />
            <textarea rows={4} value={form.motivation} onChange={(e) => set("motivation", e.target.value)}
              style={inputStyle} data-testid="input-motivation" required aria-invalid={!!errors.motivation} />
            {errors.motivation && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }} data-testid="err-motivation">{errors.motivation}</p>}
          </div>

          <div>
            <label style={labelStyle}>Gewünschte Entwicklung *</label>
            <HintNoMedical />
            <textarea rows={4} value={form.desired_development} onChange={(e) => set("desired_development", e.target.value)}
              style={inputStyle} data-testid="input-desired-development" required />
            {errors.desired_development && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }}>{errors.desired_development}</p>}
          </div>

          <div>
            <label style={labelStyle}>Praktische Umsetzungsbereitschaft *</label>
            <textarea rows={3} value={form.practical_readiness} onChange={(e) => set("practical_readiness", e.target.value)}
              style={inputStyle} data-testid="input-practical-readiness" required />
            {errors.practical_readiness && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }}>{errors.practical_readiness}</p>}
          </div>

          <div>
            <label style={labelStyle}>Verfügbare Zeit pro Woche *</label>
            <input value={form.time_available} onChange={(e) => set("time_available", e.target.value)}
              placeholder="z. B. 5–8 Stunden pro Woche"
              style={inputStyle} data-testid="input-time-available" required />
            {errors.time_available && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }}>{errors.time_available}</p>}
          </div>

          <div>
            <label className="flex items-start gap-3" style={{ color: S.ivoryMuted }}>
              <input type="checkbox" checked={form.live_participation_possible}
                onChange={(e) => set("live_participation_possible", e.target.checked)}
                data-testid="chk-live-participation" />
              <span className="text-sm">Ich kann an wöchentlichen Live-Calls teilnehmen.</span>
            </label>
          </div>

          {isMentor && (
            <>
              <div style={{ borderTop: `1px solid ${S.line}`, paddingTop: "1.5rem" }}>
                <p className="text-sm mb-4" style={{ color: S.gold }}>Zusatzangaben für SAT MENTOR</p>
              </div>
              <div>
                <label style={labelStyle}>Vorbildung *</label>
                <textarea rows={3} value={form.prior_training} onChange={(e) => set("prior_training", e.target.value)}
                  style={inputStyle} data-testid="input-prior-training" required />
                {errors.prior_training && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }} data-testid="err-prior-training">{errors.prior_training}</p>}
              </div>
              <div>
                <label style={labelStyle}>MASTER-Status *</label>
                <input value={form.master_status} onChange={(e) => set("master_status", e.target.value)}
                  placeholder="SAT MASTER abgeschlossen · anerkannte Grundlage · Override angefragt"
                  style={inputStyle} data-testid="input-master-status" required />
                {errors.master_status && <p className="text-xs mt-2" style={{ color: "#F1B5B5" }}>{errors.master_status}</p>}
              </div>
              <div>
                <label style={labelStyle}>Begleitungserfahrung</label>
                <textarea rows={3} value={form.guidance_experience} onChange={(e) => set("guidance_experience", e.target.value)}
                  style={inputStyle} data-testid="input-guidance-experience" />
              </div>
              <div>
                <label style={labelStyle}>Praxiszeit pro Woche</label>
                <input value={form.practice_time} onChange={(e) => set("practice_time", e.target.value)}
                  style={inputStyle} data-testid="input-practice-time" />
              </div>
              <div>
                <label className="flex items-start gap-3" style={{ color: S.ivoryMuted }}>
                  <input type="checkbox" checked={form.supervision_readiness}
                    onChange={(e) => set("supervision_readiness", e.target.checked)}
                    data-testid="chk-supervision" />
                  <span className="text-sm">Ich bin bereit, regelmässig an Supervision/Intervision teilzunehmen.</span>
                </label>
              </div>
            </>
          )}

          <div style={{ borderTop: `1px solid ${S.line}`, paddingTop: "1.5rem" }}>
            <label className="flex items-start gap-3 mb-4" style={{ color: S.ivoryMuted }}>
              <input type="checkbox" checked={form.privacy_consent_accepted}
                onChange={(e) => set("privacy_consent_accepted", e.target.checked)}
                data-testid="chk-privacy-consent" required aria-invalid={!!errors.privacy_consent_accepted} />
              <span className="text-sm">
                Ich habe die <a href="/legal/datenschutz" style={{ color: S.gold, textDecoration: "underline" }}>Datenschutz-Information (Version 2026-02)</a> gelesen und stimme zu. *
              </span>
            </label>
            {errors.privacy_consent_accepted && <p className="text-xs mb-4" style={{ color: "#F1B5B5" }} data-testid="err-privacy-consent">{errors.privacy_consent_accepted}</p>}
            <label className="flex items-start gap-3" style={{ color: S.ivoryMuted }}>
              <input type="checkbox" checked={form.marketing_consent}
                onChange={(e) => set("marketing_consent", e.target.checked)}
                data-testid="chk-marketing-consent" />
              <span className="text-sm">
                Ich möchte gelegentlich Impulse per E-Mail erhalten (optional, jederzeit widerrufbar).
              </span>
            </label>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="btn-submit-application"
              className="px-8 py-4 text-sm tracking-wider uppercase disabled:opacity-50"
              style={{ background: S.gold, color: S.midnight, letterSpacing: "0.16em", border: `1px solid ${S.gold}`, fontWeight: 500 }}
            >
              {submitting ? "Wird gesendet…" : (isMentor ? "Bewerbung absenden" : "Platzanfrage senden")}
            </button>
          </div>
        </form>
      </Section>
      <FooterDisclaimer />
    </div>
  );
}
